import React, { useState, useRef } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, MessageCircle, MapPin, Send } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const recaptchaRef = useRef(null);

  const onCaptchaChange = (value) => {
    if (value) {
      setCaptchaVerified(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaVerified) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }
    
    setSending(true);
    
    // Simulate sending
    setTimeout(() => {
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setCaptchaVerified(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setSending(false);
    }, 1000);
  };

  return (
    <div data-testid="contact-page">
      <SEO 
        title="Contact Us"
        description="Get in touch with ARTCARDO. Contact us for inquiries about artworks, artist partnerships, or customer support. Email: dulciaart@gmail.com"
        keywords="contact ARTCARDO, art support, customer service, buy art help"
        url="/contact"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact ARTCARDO",
          "description": "Contact page for ARTCARDO art marketplace",
          "mainEntity": {
            "@type": "Organization",
            "name": "ARTCARDO",
            "email": "dulciaart@gmail.com",
            "telephone": "+91-9019028615",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Bangalore",
              "addressCountry": "India"
            }
          }
        }}
      />
      <Navbar />
      
      <div style={{paddingTop: "180px"}}>
        <section className="section">
          <h1 className="section-title" data-testid="contact-title">Get In Touch</h1>
          <p className="section-subtitle" data-testid="contact-subtitle">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-semibold mb-6" data-testid="contact-info-title">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <div className="w-12 h-12 bg-[#f5f5f5] rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#ef4444]" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Email</h4>
                    <p className="text-gray-600">dulciaart@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-whatsapp">
                  <div className="w-12 h-12 bg-[#f5f5f5] rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-[#ef4444]" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">WhatsApp</h4>
                    <p className="text-gray-600">+91 9019028615</p>
                    <p className="text-sm text-gray-500">Mon-Fri 9am-6pm IST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-address">
                  <div className="w-12 h-12 bg-[#f5f5f5] rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#ef4444]" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Address</h4>
                    <p className="text-gray-600">
                      DULCIA.ART<br />
                      Bangalore, India
                    </p>
                    <p className="text-gray-600 mt-2">
                      <strong>Website:</strong> www.artcardo.com
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-[#f5f5f5] rounded-lg">
                <h4 className="font-semibold mb-2">Business Hours</h4>
                <p className="text-sm text-gray-600">
                  Monday - Friday: 9:00 AM - 6:00 PM IST<br />
                  Saturday: 10:00 AM - 4:00 PM IST<br />
                  Sunday: Closed
                </p>
              </div>
            </div>

            <div className="card">
              <h3 className="text-2xl font-semibold mb-6" data-testid="contact-form-title">Send Us a Message</h3>
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="form-input"
                    data-testid="name-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="form-input"
                    data-testid="email-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <Input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    className="form-input"
                    data-testid="subject-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Message</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    className="form-textarea"
                    rows={6}
                    data-testid="message-textarea"
                  />
                </div>

                <div className="form-group flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                    onChange={onCaptchaChange}
                    data-testid="recaptcha"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending || !captchaVerified}
                  className="w-full"
                  style={{background: '#ef4444', opacity: (!captchaVerified ? 0.5 : 1)}}
                  data-testid="submit-btn"
                >
                  {sending ? 'Sending...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;